import { asClusterId, asLeafKey, asModuleKey, asPlantId, asRoleKey, isAdminGrant, isExceptionKey } from '../../../access';
import type { AdminGrant, LeafKey } from '../../../access';
import { asExceptionGrantId, asOverrideId, asUserId, AUDIT_CHANGE_TYPES, VERBS, unknownValue } from '../types/domain.types';
import type {
  AccessPreview,
  AppliedChange,
  Assignable,
  AuditChangeType,
  AuditActor,
  AuditEntry,
  AuditPage,
  AuditPlantSummary,
  CascadedChange,
  Catalogue,
  CatalogueLeaf,
  CatalogueModule,
  ExceptionKeyValue,
  ExceptionRule,
  GrantValue,
  OverridesDryRun,
  PersonAccess,
  PersonListItem,
  PersonListPage,
  PlantAccess,
  PreviewMenuItem,
  StoredException,
  StoredOverride,
  UnknownValue,
  Verb,
  VerbValue,
  Violation,
  WriteResult,
} from '../types/domain.types';
import type {
  AccessPreviewDto,
  AssignableDto,
  AuditPageDto,
  AuditPlantsDto,
  GrantAdminTreeDto,
  LicenceListDto,
  MenuItemDto,
  OverridesDryRunDto,
  PeoplePageDto,
  PersonAccessDto,
  TreeDto,
  WriteResultDto,
} from '../api/schemas';

type OverrideDto = PersonAccessDto['plants'][number]['overrides'][number];
type ExceptionDto = PersonAccessDto['plants'][number]['exceptions'][number];

const KNOWN_VERBS = new Set<string>(VERBS);
const KNOWN_CHANGE_TYPES = new Set<string>(AUDIT_CHANGE_TYPES);

const verbOf = (raw: string): VerbValue => (KNOWN_VERBS.has(raw) ? (raw as Verb) : unknownValue(raw));

export const grantValueOf = (raw: string | null): GrantValue | null => {
  if (raw === null) return null;
  return isAdminGrant(raw) ? raw : unknownValue(raw);
};

const exceptionKeyOf = (raw: string): ExceptionKeyValue => (isExceptionKey(raw) ? raw : unknownValue(raw));

const changeTypeOf = (raw: string): AuditChangeType | UnknownValue =>
  KNOWN_CHANGE_TYPES.has(raw) ? (raw as AuditChangeType) : unknownValue(raw);

const overrideOf = (o: OverrideDto): StoredOverride => ({
  overrideId: asOverrideId(o.overrideId),
  key: asLeafKey(o.permissionKey),
  effect: o.effect,
  reason: o.reason,
});

const exceptionOf = (e: ExceptionDto): StoredException => ({
  grantId: asExceptionGrantId(e.grantId),
  key: exceptionKeyOf(e.exceptionKey),
  reason: e.reason,
  expiresAt: e.expiresAt,
  expired: e.expired,
});

export const normalizeCatalogue = (dto: TreeDto): Catalogue => {
  const fencedKeys = new Set<LeafKey>(dto.fencedKeys.map(asLeafKey));
  const leavesByKey = new Map<LeafKey, CatalogueLeaf>();
  const alwaysOnModules = new Set(dto.modules.filter((m) => m.alwaysOn).map((m) => asModuleKey(m.moduleKey)));

  const modules: CatalogueModule[] = dto.modules.map((m) => {
    const moduleKey = asModuleKey(m.moduleKey);
    const features = m.features.map((f) => {
      const crud = f.crud.map((c): CatalogueLeaf => {
        const key = asLeafKey(c.key);
        return {
          key,
          module: moduleKey,
          feature: f.featureKey,
          featureLabel: f.label,
          verb: verbOf(c.verb),
          label: f.label,
          plane: 'job',
          isAction: false,
          fenced: fencedKeys.has(key),
          legacyAlias: null,
          tier: null,
        };
      });
      const actions = f.actions.map((a): CatalogueLeaf => {
        const key = asLeafKey(a.key);
        return {
          key,
          module: moduleKey,
          feature: f.featureKey,
          featureLabel: f.label,
          verb: null,
          label: a.label,
          plane: a.adminPlane ? 'admin' : f.plane,
          isAction: true,
          fenced: a.fenced || fencedKeys.has(key),
          legacyAlias: a.legacyAlias,
          tier: a.tier,
        };
      });
      const leaves = [...crud, ...actions];
      for (const leaf of leaves) leavesByKey.set(leaf.key, leaf);
      return { featureKey: f.featureKey, label: f.label, plane: f.plane, leaves };
    });
    return { moduleKey, label: m.label, alwaysOn: m.alwaysOn, features };
  });

  const exceptionRules: ExceptionRule[] = [];
  for (const e of dto.exceptionPermissions) {
    if (isExceptionKey(e.key)) exceptionRules.push({ key: e.key, requiresModule: e.requiresModule ? asModuleKey(e.requiresModule) : null });
  }

  return {
    modules,
    leavesByKey,
    fencedKeys,
    alwaysOnModules,
    exceptionKeys: exceptionRules.map((r) => r.key),
    exceptionRules,
  };
};

export interface GrantAdminTree {
  readonly tickedKeys: ReadonlySet<LeafKey>;
  readonly ticked: number;
  readonly total: number;
}

export const normalizeGrantAdminTree = (dto: GrantAdminTreeDto): GrantAdminTree => {
  const tickedKeys = new Set<LeafKey>();
  for (const m of dto.modules) for (const f of m.features) for (const a of f.actions) if (a.ticked) tickedKeys.add(asLeafKey(a.key));
  return { tickedKeys, ticked: dto.totals.tickedByGrant, total: dto.totals.adminPlane };
};

const clusterOf = (value: PersonAccessDto['adminGrantClusterId']): { readonly id: string | null; readonly name: string | null } => {
  if (value === null) return { id: null, name: null };
  if (typeof value === 'string') return { id: value, name: null };
  return { id: value.clusterId, name: value.name };
};

export const normalizePersonAccess = (dto: PersonAccessDto): PersonAccess => {
  const cluster = clusterOf(dto.adminGrantClusterId);
  const plants: PlantAccess[] = dto.plants.map((p) => ({
    plantId: asPlantId(p.plantId),
    name: p.name,
    assigned: p.assigned,
    outOfCluster: p.outOfCluster,
    licensedModules: p.licensedModules.map(asModuleKey),
    overrides: p.overrides.map(overrideOf),
    exceptions: p.exceptions.map(exceptionOf),
    effectiveKeys: new Set<LeafKey>(p.effectiveKeys.map(asLeafKey)),
  }));

  return {
    userId: asUserId(dto.userId),
    name: dto.name,
    email: dto.email,
    title: dto.title,
    company: dto.company,
    landing: dto.landing,
    roleKey: dto.roleKey ? asRoleKey(dto.roleKey) : null,
    standardKeys: new Set<LeafKey>(dto.roleKeys.map(asLeafKey)),
    adminGrant: grantValueOf(dto.adminGrant),
    adminGrantClusterId: cluster.id ? asClusterId(cluster.id) : null,
    adminGrantClusterName: dto.adminGrantClusterName ?? cluster.name ?? dto.clusterName,
    adminKeys: new Set<LeafKey>(dto.adminKeys.map(asLeafKey)),
    permissionsVersion: dto.permissionsVersion,
    plants,
    globalOverrides: dto.globalOverrides.map(overrideOf),
  };
};

export const normalizePeoplePage = (dto: PeoplePageDto): PersonListPage => ({
  items: dto.items.map(
    (i): PersonListItem => ({
      userId: asUserId(i.userId),
      name: i.name,
      email: i.email,
      title: i.title,
      company: i.company,
      roleKey: i.roleKey ? asRoleKey(i.roleKey) : null,
      adminGrant: grantValueOf(i.adminGrant),
      clusterId: i.clusterId ? asClusterId(i.clusterId) : null,
      clusterName: i.clusterName,
      userStatus: i.userStatus,
      plants: i.plants.map((p) => ({ plantId: asPlantId(p.plantId), name: p.name })),
      overrideCount: i.overrideCount,
      exceptionCount: i.exceptionCount,
    }),
  ),
  total: dto.total,
  page: dto.page,
  limit: dto.limit,
});

const RAW_USER_ACTOR_PREFIX = 'user:';

const actorOf = (actorName: string | null, actorLabel: string): AuditActor => {
  if (actorName) return { kind: 'named', name: actorName };
  if (actorLabel === '' || actorLabel.startsWith(RAW_USER_ACTOR_PREFIX)) return { kind: 'unknown' };
  return { kind: 'named', name: actorLabel };
};

export const normalizeAuditPage = (dto: AuditPageDto): AuditPage => ({
  window: dto.window,
  items: dto.items.map(
    (e): AuditEntry => ({
      auditId: e.auditId,
      createdAt: e.createdAt,
      changeType: changeTypeOf(e.changeType),
      actor: actorOf(e.actor.name, e.actor.label),
      plant: e.plant ? { id: asPlantId(e.plant.id), name: e.plant.name } : null,
      target: e.target ? { id: asUserId(e.target.id), name: e.target.name } : null,
      change: e.change,
      permissionKey: e.permissionKey,
      before: e.before,
      after: e.after,
      reason: e.reason,
    }),
  ),
  total: dto.total,
  nextCursor: dto.nextCursor,
  limit: dto.limit,
  changeTypes: dto.changeTypes.filter((c): c is AuditChangeType => KNOWN_CHANGE_TYPES.has(c)),
});

export const normalizeAuditPlants = (dto: AuditPlantsDto): AuditPlantSummary => ({
  window: dto.window,
  total: dto.total,
  plants: dto.plants.map((p) => ({ ...p, plantId: asPlantId(p.plantId) })),
});

const menuItemOf = (item: MenuItemDto): PreviewMenuItem => ({
  name: item.name,
  link: item.link ?? null,
  children: (item.children ?? []).map(menuItemOf),
});

export const normalizeAccessPreview = (dto: AccessPreviewDto): AccessPreview => ({
  plantId: asPlantId(dto.plantId),
  menu: dto.menu.map(menuItemOf),
  landing: dto.landing,
});

const appliedOf = (c: OverridesDryRunDto['applied'][number]): AppliedChange => ({
  permissionKey: asLeafKey(c.permissionKey),
  effect: c.effect,
});

const cascadedOf = (c: OverridesDryRunDto['cascaded'][number]): CascadedChange => ({
  permissionKey: asLeafKey(c.permissionKey),
  effect: c.effect,
  causedBy: c.causedBy === null ? null : asLeafKey(c.causedBy),
});

export const violationOf = (v: { code: string; message?: string | null; keys?: readonly string[] }): Violation => ({
  code: v.code,
  message: v.message ?? null,
  keys: (v.keys ?? []).map(asLeafKey),
});

export const normalizeDryRun = (dto: OverridesDryRunDto): OverridesDryRun => ({
  applied: dto.applied.map(appliedOf),
  cascaded: dto.cascaded.map(cascadedOf),
  violations: dto.violations.map(violationOf),
});

export const normalizeWriteResult = (dto: WriteResultDto): WriteResult => ({
  changed: dto.changed,
  sessionsRevoked: dto.sessionsRevoked,
  forcedReLogin: dto.forcedReLogin,
  overridesCleared: dto.overridesCleared,
  exceptionsRevoked: dto.exceptionsRevoked,
  message: dto.message,
  applied: dto.applied.map(appliedOf),
  cascaded: dto.cascaded.map(cascadedOf),
});

export const normalizeAssignable = (dto: AssignableDto): Assignable => ({
  roles: dto.roles.map((r) => ({ roleKey: asRoleKey(r.roleKey), name: r.name || r.roleKey })),
  grants: dto.grants.filter((g): g is AdminGrant => isAdminGrant(g)),
});

export type LicenceList = LicenceListDto;
